import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Loader2, MessageSquare, Phone, Video, Clock, User, Calendar,
  CheckCircle, XCircle, AlertCircle, DollarSign, Eye
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  CONFIRMED: 'bg-blue-100 text-blue-800',
  IN_PROGRESS: 'bg-purple-100 text-purple-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-800',
  NO_SHOW: 'bg-gray-100 text-gray-800'
};

const PAYMENT_STATUS_COLORS = {
  UNPAID: 'bg-red-100 text-red-800',
  PARTIAL: 'bg-yellow-100 text-yellow-800',
  PAID: 'bg-green-100 text-green-800',
  FREE: 'bg-gray-100 text-gray-800'
};

const TYPE_ICONS = {
  TEXT: MessageSquare,
  AUDIO: Phone,
  VIDEO: Video
};

export default function ConsultationsPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [consultations, setConsultations] = useState([]);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [providerNotes, setProviderNotes] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    loadConsultations();
  }, []);

  const loadConsultations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/consultations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConsultations(response.data || []);
    } catch (error) {
      console.error('Error loading consultations:', error);
      toast.error('Failed to load consultations');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (bookingId) => {
    try {
      const response = await axios.get(`${API_URL}/api/consultation-bookings/${bookingId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data || []);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const selectConsultation = (consultation) => {
    setSelectedConsultation(consultation);
    setProviderNotes(consultation.providerNotes || '');
    loadMessages(consultation.id);
  };

  const updateStatus = async (bookingId, status) => {
    try {
      await axios.patch(`${API_URL}/api/dashboard/consultations/${bookingId}`, 
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Consultation ${status.toLowerCase()}`);
      loadConsultations();
      if (selectedConsultation?.id === bookingId) {
        setSelectedConsultation({ ...selectedConsultation, status });
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const updatePaymentStatus = async (bookingId, paymentStatus) => {
    try {
      await axios.patch(`${API_URL}/api/dashboard/consultations/${bookingId}`, 
        { paymentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Payment status updated');
      loadConsultations();
    } catch (error) {
      console.error('Error updating payment:', error);
      toast.error('Failed to update payment status');
    }
  };

  const saveProviderNotes = async () => {
    if (!selectedConsultation) return;
    try {
      await axios.patch(`${API_URL}/api/dashboard/consultations/${selectedConsultation.id}`, 
        { providerNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Notes saved');
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Failed to save notes');
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConsultation) return;
    setSendingMessage(true);
    try {
      await axios.post(
        `${API_URL}/api/consultation-bookings/${selectedConsultation.id}/messages`,
        { message: newMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewMessage('');
      loadMessages(selectedConsultation.id);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const filterConsultations = (status) => {
    if (status === 'pending') {
      return consultations.filter(c => c.status === 'PENDING');
    } else if (status === 'active') {
      return consultations.filter(c => ['CONFIRMED', 'IN_PROGRESS'].includes(c.status));
    } else {
      return consultations.filter(c => ['COMPLETED', 'CANCELLED', 'NO_SHOW'].includes(c.status));
    }
  };

  const formatPrice = (cents) => {
    if (!cents) return 'Free';
    return `$${(cents / 100).toFixed(2)}`;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  const ConsultationCard = ({ consultation }) => {
    const TypeIcon = TYPE_ICONS[consultation.consultationType] || MessageSquare;
    return (
      <Card 
        className={`cursor-pointer transition-colors hover:border-primary ${
          selectedConsultation?.id === consultation.id ? 'border-primary bg-primary/5' : ''
        }`}
        onClick={() => selectConsultation(consultation)}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-muted rounded-full">
                <TypeIcon className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium">{consultation.clientName || 'Client'}</p>
                <p className="text-sm text-muted-foreground">{consultation.clientEmail}</p>
              </div>
            </div>
            <Badge className={STATUS_COLORS[consultation.status]}>
              {consultation.status}
            </Badge>
          </div>
          <div className="mt-3 flex items-center space-x-4 text-sm text-muted-foreground">
            <span className="flex items-center">
              <Calendar className="h-3 w-3 mr-1" />
              {consultation.scheduledDate || 'Open Door'}
            </span>
            {consultation.scheduledStart && (
              <span className="flex items-center">
                <Clock className="h-3 w-3 mr-1" />
                {consultation.scheduledStart}
              </span>
            )}
            <span className="flex items-center">
              <DollarSign className="h-3 w-3 mr-1" />
              {formatPrice(consultation.price)}
            </span>
          </div>
          {consultation.clientQuestion && (
            <p className="mt-2 text-sm line-clamp-2 text-muted-foreground">
              "{consultation.clientQuestion}"
            </p>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Consultations</h1>
          <p className="text-muted-foreground">Manage your consultation requests and sessions</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Consultation List */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="pending">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="pending" className="relative">
                  Pending
                  {filterConsultations('pending').length > 0 && (
                    <span className="ml-2 bg-yellow-500 text-white text-xs rounded-full px-2">
                      {filterConsultations('pending').length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="active">Active</TabsTrigger>
                <TabsTrigger value="history">History</TabsTrigger>
              </TabsList>

              <TabsContent value="pending" className="space-y-4 mt-4">
                {filterConsultations('pending').length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center text-muted-foreground">
                      <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                      <p>No pending consultation requests</p>
                    </CardContent>
                  </Card>
                ) : (
                  filterConsultations('pending').map((c) => (
                    <ConsultationCard key={c.id} consultation={c} />
                  ))
                )}
              </TabsContent>

              <TabsContent value="active" className="space-y-4 mt-4">
                {filterConsultations('active').length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center text-muted-foreground">
                      <p>No active consultations</p>
                    </CardContent>
                  </Card>
                ) : (
                  filterConsultations('active').map((c) => (
                    <ConsultationCard key={c.id} consultation={c} />
                  ))
                )}
              </TabsContent>

              <TabsContent value="history" className="space-y-4 mt-4">
                {filterConsultations('history').length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center text-muted-foreground">
                      <p>No consultation history</p>
                    </CardContent>
                  </Card>
                ) : (
                  filterConsultations('history').map((c) => (
                    <ConsultationCard key={c.id} consultation={c} />
                  ))
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-1">
            {selectedConsultation ? (
              <Card className="sticky top-4">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Consultation Details</CardTitle>
                    <Badge className={STATUS_COLORS[selectedConsultation.status]}>
                      {selectedConsultation.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Client Info */}
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Client</p>
                    <p className="font-medium">{selectedConsultation.clientName || 'Client'}</p>
                    <p className="text-sm">{selectedConsultation.clientEmail}</p>
                    {selectedConsultation.clientPhone && (
                      <p className="text-sm">{selectedConsultation.clientPhone}</p>
                    )}
                  </div>

                  {/* Schedule */}
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Schedule</p>
                    <p>{selectedConsultation.scheduledDate || 'Open Door'}</p>
                    {selectedConsultation.scheduledStart && (
                      <p>{selectedConsultation.scheduledStart} - {selectedConsultation.scheduledEnd}</p>
                    )}
                  </div>

                  {/* Type */}
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Type</p>
                    <p className="capitalize">{selectedConsultation.consultationType?.toLowerCase()}</p>
                  </div>

                  {/* Price & Payment */}
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Payment</p>
                    <div className="flex items-center space-x-2">
                      <span>{formatPrice(selectedConsultation.price)}</span>
                      <Badge className={PAYMENT_STATUS_COLORS[selectedConsultation.paymentStatus]}>
                        {selectedConsultation.paymentStatus}
                      </Badge>
                    </div>
                    {selectedConsultation.paymentStatus !== 'FREE' && selectedConsultation.paymentStatus !== 'PAID' && (
                      <Select
                        value={selectedConsultation.paymentStatus}
                        onValueChange={(value) => updatePaymentStatus(selectedConsultation.id, value)}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Update payment" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="UNPAID">Unpaid</SelectItem>
                          <SelectItem value="PARTIAL">Partial</SelectItem>
                          <SelectItem value="PAID">Paid</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {/* Client Question */}
                  {selectedConsultation.clientQuestion && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Client's Question</p>
                      <p className="text-sm bg-muted p-2 rounded">{selectedConsultation.clientQuestion}</p>
                    </div>
                  )}

                  {/* Actions */}
                  {selectedConsultation.status === 'PENDING' && (
                    <div className="flex space-x-2">
                      <Button 
                        className="flex-1" 
                        onClick={() => updateStatus(selectedConsultation.id, 'CONFIRMED')}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" /> Accept
                      </Button>
                      <Button 
                        variant="destructive" 
                        className="flex-1"
                        onClick={() => updateStatus(selectedConsultation.id, 'CANCELLED')}
                      >
                        <XCircle className="h-4 w-4 mr-2" /> Decline
                      </Button>
                    </div>
                  )}

                  {selectedConsultation.status === 'CONFIRMED' && (
                    <div className="flex space-x-2">
                      <Button 
                        className="flex-1" 
                        onClick={() => updateStatus(selectedConsultation.id, 'IN_PROGRESS')}
                      >
                        Start Session
                      </Button>
                    </div>
                  )}

                  {selectedConsultation.status === 'IN_PROGRESS' && (
                    <div className="flex space-x-2">
                      <Button 
                        className="flex-1" 
                        onClick={() => updateStatus(selectedConsultation.id, 'COMPLETED')}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" /> Complete
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => updateStatus(selectedConsultation.id, 'NO_SHOW')}
                      >
                        No Show
                      </Button>
                    </div>
                  )}

                  {/* Provider Notes */}
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Private Notes</p>
                    <Textarea
                      value={providerNotes}
                      onChange={(e) => setProviderNotes(e.target.value)}
                      placeholder="Notes visible only to you..."
                      rows={3}
                    />
                    <Button size="sm" className="mt-2" onClick={saveProviderNotes}>
                      Save Notes
                    </Button>
                  </div>

                  {/* Messages (for TEXT consultations) */}
                  {selectedConsultation.consultationType === 'TEXT' && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-2">Messages</p>
                      <div className="max-h-48 overflow-y-auto space-y-2 mb-2">
                        {messages.map((msg) => (
                          <div 
                            key={msg.id}
                            className={`p-2 rounded text-sm ${
                              msg.senderRole === 'PROVIDER' 
                                ? 'bg-primary text-primary-foreground ml-4' 
                                : 'bg-muted mr-4'
                            }`}
                          >
                            <p>{msg.message}</p>
                            <p className="text-xs opacity-70 mt-1">
                              {new Date(msg.createdAt).toLocaleString()}
                            </p>
                          </div>
                        ))}
                      </div>
                      {!['CANCELLED', 'NO_SHOW'].includes(selectedConsultation.status) && (
                        <div className="flex space-x-2">
                          <Textarea
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder="Type a message..."
                            rows={2}
                            className="flex-1"
                          />
                          <Button onClick={sendMessage} disabled={sendingMessage || !newMessage.trim()}>
                            {sendingMessage ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send'}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <Eye className="h-8 w-8 mx-auto mb-2" />
                  <p>Select a consultation to view details</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
